import { lazy, Suspense, type ComponentType, type ReactElement } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import App from './App'
import Home from './pages/Home'
import NotFound from './pages/NotFound'
import { PageOverlay } from '@/components/PageOverlay'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { SERVICE_PAGE_SLUGS } from '@/content/servicePages'

const About = lazy(() => import('./pages/About'))
const Services = lazy(() => import('./pages/Services'))
const Portfolio = lazy(() => import('./pages/Portfolio'))
const Insights = lazy(() => import('./pages/Insights'))
const Article = lazy(() => import('./pages/Article'))
const InsightTopic = lazy(() => import('./pages/InsightTopic'))
const Workshops = lazy(() => import('./pages/Speaking'))
const Contact = lazy(() => import('./pages/Contact'))
const Privacy = lazy(() => import('./pages/Privacy'))
const ServicesRedirect = lazy(() => import('./pages/ServicesRedirect'))
const ServicePage = lazy(() => import('./pages/ServicePage'))
const AdminPage = lazy(() => import('./pages/Admin'))

function lazyRoute(Component: ComponentType): ReactElement {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageOverlay isVisible />}>
        <Component />
      </Suspense>
    </ErrorBoundary>
  )
}

/**
 * Specialist advisory pages share one template, so they are generated from the
 * canonical slug list rather than hand-written four times.
 *
 * Deliberately EXPLICIT routes, not a `:slug` catch-all: a catch-all at this
 * level would swallow every unmatched top-level path and starve the `*` 404
 * route below it. An unpublished page still resolves here — ServicePage renders
 * NotFound for anyone who is not an admin.
 */
const servicePageRoutes = SERVICE_PAGE_SLUGS.map((slug) => ({
  path: slug,
  element: (
    <ErrorBoundary>
      <Suspense fallback={<PageOverlay isVisible />}>
        <ServicePage slug={slug} />
      </Suspense>
    </ErrorBoundary>
  ),
}))

export const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    errorElement: (
      <ErrorBoundary>
        <NotFound />
      </ErrorBoundary>
    ),
    children: [
      { index: true, element: <Home /> },
      { path: 'about', element: lazyRoute(About) },
      { path: 'services', element: lazyRoute(Services) },
      // Legacy /services/<slug> URLs redirect to anchor on /services
      // (no per-service detail pages exist — anchors keep deep links intact).
      { path: 'services/:slug', element: lazyRoute(ServicesRedirect) },
      ...servicePageRoutes,
      { path: 'portfolio', element: lazyRoute(Portfolio) },
      { path: 'insights', element: lazyRoute(Insights) },
      // Topic hubs sit ABOVE the article route in specificity, so React Router
      // ranks the three-segment path first and `/insights/:slug` is untouched —
      // existing article URLs keep working, which is acceptance criterion #7.
      // The bare path is also 301'd server-side (server/lib/redirects.ts); this
      // route covers in-app navigation, which never reaches the server.
      { path: 'insights/topics', element: <Navigate to="/insights" replace /> },
      { path: 'insights/topics/:topic', element: lazyRoute(InsightTopic) },
      { path: 'insights/:slug', element: lazyRoute(Article) },
      // Renamed from /speaking, which the server 301s here. The component keeps
      // its filename: the page is the same evidence (19 speaking events, the
      // Three & Company block), expanded with workshop formats — not a new page.
      { path: 'workshops', element: lazyRoute(Workshops) },
      { path: 'contact', element: lazyRoute(Contact) },
      { path: 'privacy', element: lazyRoute(Privacy) },
      { path: 'admin/*', element: lazyRoute(AdminPage) },
      { path: '*', element: <NotFound /> },
    ],
  },
])
